import { formatProductionDate } from "@/utils/date-time-formatter";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { StatusTitle } from "../../../shared/data-entries/StatusTitle";
import { dataEntryStyles } from "../../../shared/styles/dataEntryStyles";
import { ProcessNavigator } from "@/shared/data-entries/ProcessNavigator";
import { TableChemistry } from "./components/tables/chemistries/TableChemistry";
import { useSaveChemistryResultMutation } from "./hooks/mutations/useSaveChemistryResultMutation";
import { useSaveSampleAttributesMutation } from "./hooks/mutations/useSaveSampleAttributesMutation";
import { useLabProcessQuery } from "./hooks/queries/useLabProcessQuery";
import {
  ChemistrySample,
  Process,
  ProcessResponse,
  SampleAttributesPayload,
} from "./models/lab.models";
import {
  getLabProcess,
  getLastestProcesses,
  searchProcesses,
} from "./repositories/lab-de.repositories";

export const LabDataEntryPage = () => {
  const theme = useTheme();
  const styles = dataEntryStyles(theme);
  const navigate = useNavigate();
  const params = useParams();

  const processId = params.processId ? Number(params.processId) : null;
  const [isEditing, setIsEditing] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { data: deProcess } = useLabProcessQuery(processId, isEditing);

  const saveChemistryMutation = useSaveChemistryResultMutation();
  const saveSampleAttributesMutation = useSaveSampleAttributesMutation();

  const process = deProcess?.process;

  const pauseRefresh = () => setIsEditing(true);
  const resumeRefresh = () => setIsEditing(false);

  const loadData = async (processIdToLoad: number | null) => {
    if (processIdToLoad != null) {
      navigate(`/lab-data-entry/${processIdToLoad}`);
      return;
    }
    try {
      const resp = await getLabProcess({ ProcessId: null });
      if (!resp.ok) throw new Error(resp.message ?? "Failed to load process");
      const data = resp.data as ProcessResponse;
      const latestProcess = data.tables["0"].rows[0] as Process;
      navigate(`/lab-data-entry/${latestProcess.ProcessId}`);
    } catch (error: unknown) {
      if (error instanceof Error) setErrorMessage(error.message);
    }
  };

  const handleUpsertChemistry = async (
    chemistrySampleId: number,
    elementId: number,
    value: number | null,
  ) => {
    try {
      await saveChemistryMutation.mutateAsync({
        ChemistrySampleId: chemistrySampleId,
        ElementId: elementId,
        Value: value,
      });
      setNotificationMessage("Chemistry saved successfully");
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(
          error.message || "An error occurred while saving the chemistry data.",
        );
      }
    }
  };

  const handleSampleAttributesSubmit = async (
    data: ChemistrySample,
    selectedChemistrySampleId: number | null,
  ) => {
    try {
      const payload: SampleAttributesPayload = {
        ChemistrySampleId: selectedChemistrySampleId,
        SampleTypeId: data.SampleTypeId!,
        SampleSequence: data.SampleSequence,
        HeatId: process?.HeatId!,
        DateTimeResults: data.Reported,
        Comments: data.Comments || "",
        Technician: data.Technician,
      };
      await saveSampleAttributesMutation.mutateAsync(payload);
      setNotificationMessage("Sample attributes saved successfully");
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message || "Cannot save sample attributes.");
      } else {
        setErrorMessage("Cannot save sample attributes.");
      }
    }
  };

  const handleProcessNavigation = () => {
    if (process?.HeatStatusCode === "M" || process?.HeatStatusCode === "T") {
      navigate(
        `/eaf-processes/${process?.ShiftId}?processId=${process?.ProcessId}`,
      );
    } else if (
      process?.HeatStatusCode === "L" ||
      process?.HeatStatusCode === "U"
    ) {
      navigate(
        `/lmf-processes/${process?.ShiftId}?processId=${process?.ProcessId}`,
      );
    } else {
      navigate(
        `/caster-processes/${process?.ShiftId}?processId=${process?.ProcessId}`,
      );
    }
  };

  return (
    <>
      <HelmetDexteel
        title={`${
          process?.HeatName ? `${process.HeatName} - ` : ""
        }Chemistries Data Entry`}
      />
      <Box padding={2} style={{ height: "calc(100vh - 64px)" }}>
        <Grid container spacing={1} direction={"column"}>
          <Grid item>
            <Typography variant={"h5"} sx={{ fontWeight: 600 }}>
              Chemistries Data Entry
            </Typography>
          </Grid>
          <Grid item>
            <Paper>
              <Box padding={1}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  spacing={3}
                >
                  <Grid item xs={12} md={2}>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <ProcessNavigator
                          key={`lab-de-process-navigator`}
                          process={deProcess?.process ?? null}
                          loadData={loadData}
                          getLatestProcesses={getLastestProcesses}
                          searchProcesses={searchProcesses}
                          pause={pauseRefresh}
                          resume={resumeRefresh}
                          onError={setErrorMessage}
                          initialProcessId={
                            params.processId
                              ? Number(params.processId)
                              : undefined
                          }
                        />
                      </Grid>
                      {process?.HeatStatusCode && process?.AssetId ? (
                        <Grid item>
                          <StatusTitle
                            heatStatus={process?.HeatStatusCode ?? ""}
                          />
                        </Grid>
                      ) : null}
                    </Grid>
                  </Grid>
                  <Grid
                    key={`lab-data-entry-data-${process?.ProcessId}`}
                    item
                    xs={12}
                    md={6}
                  >
                    <Grid
                      container
                      direction="row"
                      spacing={3}
                      justifyContent="center"
                    >
                      <Grid item xs={12} sm={4} md={4}>
                        <TextField
                          fullWidth
                          label={"Shift"}
                          variant={"outlined"}
                          disabled
                          value={`${formatProductionDate(process?.ProductionDate)} ${process?.Shift} - ${process?.Crew}`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={4}>
                        <TextField
                          fullWidth
                          label={"Grades"}
                          variant={"outlined"}
                          disabled
                          value={process?.GradeName || ""}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={4}>
                        <TextField
                          fullWidth
                          label={"Ladle"}
                          variant={"outlined"}
                          disabled
                          value={process?.LadleName || ""}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`lab-data-entry-buttons-${process?.ProcessId}`}
                    item
                    xs={12}
                    md={2}
                    style={{ marginTop: 5 }}
                  >
                    <Button
                      fullWidth
                      variant={"outlined"}
                      size="large"
                      onClick={() => {
                        handleProcessNavigation();
                      }}
                      sx={styles.buttons}
                    >
                      Processes
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
            <Grid item xs={12} style={{ marginTop: 10 }}>
              <TableChemistry
                key={`lab-data-entry-${deProcess?.process.HeatId}`}
                heatId={deProcess?.process?.HeatId ?? null}
                gradeId={deProcess?.process?.GradeId ?? null}
                editable
                onCellValueChange={handleUpsertChemistry}
                onCellEditingStarted={pauseRefresh}
                onCellEditingStopped={resumeRefresh}
                onSubmit={handleSampleAttributesSubmit}
                showSampleAttributes={true}
                addNewSample={true}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Unexpected error"
      />
    </>
  );
};

export default LabDataEntryPage;
